import React from 'react';
import { useQuery } from '@apollo/client';
import getPlatformHealth from './getPlatformHealth.graphql';

const RED = '#D32F2F';
const TEAL = '#0E9488';
const INK = '#1F2733';

// Dependency-free SVG line chart with area fill.
function LineChart({ points, color, height = 150, label, fmt }) {
  const W = 560; const H = height; const pad = 28;
  const vals = points.map((p) => p.y);
  const max = Math.max(1, ...vals);
  const min = Math.min(0, ...vals);
  const n = points.length;
  const x = (i) => pad + (n <= 1 ? 0 : (i * (W - pad * 2)) / (n - 1));
  const y = (v) => H - pad - ((v - min) / (max - min || 1)) * (H - pad * 2);
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.y).toFixed(1)}`).join(' ');
  const area = `${line} L ${x(n - 1).toFixed(1)} ${H - pad} L ${x(0).toFixed(1)} ${H - pad} Z`;
  const last = points[n - 1];
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 14, flex: '1 1 380px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontWeight: 700, color: INK }}>{label}</span>
        <span style={{ fontWeight: 800, color }}>{last ? (fmt ? fmt(last.y) : last.y) : '—'}</span>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <path d={area} fill={color} opacity="0.12" />
        <path d={line} fill="none" stroke={color} strokeWidth="2.5" />
        {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.y)} r="2" fill={color} />)}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#999', marginTop: 4 }}>
        <span>{points[0] && points[0].label}</span>
        <span>{last && last.label}</span>
      </div>
    </div>
  );
}

export default function PlatformHealth() {
  const { data, loading } = useQuery(getPlatformHealth, { fetchPolicy: 'network-only', ssr: false });
  const h = data && data.getPlatformHealth;
  if (loading && !h) return <div style={{ fontFamily: 'Poppins, sans-serif', padding: 8, color: '#999' }}>Loading platform health…</div>;
  if (!h || h.status !== 200) return null;

  const sign = (h.currency === 'USD') ? '$' : '';
  const money = (v) => `${sign}${v != null ? v.toLocaleString() : '—'}`;
  const daily = h.daily || [];
  const revPts = daily.map((d) => ({ label: d.date ? d.date.slice(5) : '', y: d.revenue }));
  const bkPts = daily.map((d) => ({ label: d.date ? d.date.slice(5) : '', y: d.bookings }));

  const card = (label, value, color, sub) => (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '14px 16px', minWidth: 140, flex: '1 1 140px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || INK }}>{value}</div>
      <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
      {sub ? <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{sub}</div> : null}
    </div>
  );

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif', marginBottom: 24 }}>
      <h2 style={{ fontSize: 18, fontWeight: 800, color: INK, margin: '4px 0 12px' }}>Platform health <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>· last 30 days</span></h2>

      {/* Money */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        {card('GMV (30d)', money(h.gmvMonth), RED, `all-time ${money(h.gmvAllTime)}`)}
        {card('Worker revenue (30d)', money(h.workerRevenueMonth), '#1565C0', `all-time ${money(h.workerRevenueAllTime)}`)}
        {card('Platform revenue (30d)', money(h.platformRevenueMonth), TEAL, 'your commissions')}
        {card('Bookings (30d)', h.bookingsMonth, INK, `today ${h.bookingsToday}`)}
        {card('Cancel rate', `${h.cancelRate}%`, h.cancelRate > 20 ? '#C62828' : INK, `${h.cancelledMonth} cancelled`)}
      </div>

      {/* Workers + signals */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        {card('Providers online', h.providersOnline, '#2E7D32', `${h.providersOffline} offline`)}
        {card('Total providers', h.totalProviders, INK, `+${h.newProviders30d} in 30d`)}
        {card('Total customers', h.totalCustomers, INK, `+${h.newCustomers30d} in 30d`)}
        {card('Open disputes', h.openDisputes, h.openDisputes ? '#C62828' : '#2E7D32')}
        {card('Open tickets', h.openTickets, h.openTickets ? '#F9A825' : '#2E7D32')}
        {card('Pending refunds', h.pendingRefunds, h.pendingRefunds ? '#F9A825' : '#2E7D32')}
      </div>

      {/* Charts */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <LineChart points={revPts} color={RED} label="Daily revenue (GMV)" fmt={(v) => money(v)} />
        <LineChart points={bkPts} color={TEAL} label="Daily bookings" />
      </div>
    </div>
  );
}
